import { z } from "zod";

/** What a source lifecycle state does in the destination CRM (spec §7). */
export type StateAction = "sync" | "archive" | "skip";

export interface StateRule {
  /** Source lifecycle state (CANCELLED, CONFIRMED, …). Matched case-insensitively. */
  state: string;
  action: StateAction;
  /** Stage id to set on a sync. Only valid where the destination object has a
   *  stage field — hubspot.deal → dealstage. Absent means "write the record but
   *  leave its stage untouched", which is a first-class choice, not a missing value. */
  stage?: string;
}

export interface StateMapping {
  version: 1;
  mode?: "active" | "dry-run";
  rules: StateRule[];
  /** A state no rule names. Deliberately NOT allowed to be "archive": an
   *  unrecognised state must never delete anything (spec Goal 4 meets Goal 3). */
  unmapped: "sync" | "skip";
}

/** Ceiling on `rules`. Exported so `stateMappingFromStageMap` refuses to build a
 *  mapping the schema would then reject, rather than the two drifting apart. */
export const MAX_STATE_RULES = 50;

/**
 * Runtime v1 gate for a persisted/posted state mapping (spec §5). Mirrors
 * `FieldMappingSchema`'s `{ version, rules[], <fallback> }` shape so operators
 * and validators meet an idiom they already know.
 *
 * Shape only. The rules that need registry metadata to judge — a stage on an
 * object with no stage field, an archive on an object that may not be archived,
 * a duplicate state — live in `validateStateMapping`.
 *
 * Declared with `satisfies` rather than a `z.ZodType<StateMapping>` annotation:
 * the latter would erase the concrete `z.ZodObject` type and remove
 * `.extend`/`.shape`/`.omit` from the value, which a later task needs.
 */
export const StateMappingSchema = z.object({
  version: z.literal(1),
  /** "dry-run" resolves every decision and logs what it WOULD do, then behaves
   *  exactly as if no mapping existed (spec §15.2). The editor writes it on
   *  first enable, so a mapping cannot touch a CRM before an operator has seen
   *  its decisions.
   *
   *  Optional, not `.default("active")`: every consumer asks `mode === "dry-run"`,
   *  so an absent mode is active, and mapping literals written before this field
   *  existed still typecheck. */
  mode: z.enum(["active", "dry-run"]).optional(),
  rules: z
    .array(
      z.object({
        state: z.string().min(1),
        action: z.enum(["sync", "archive", "skip"]),
        stage: z.string().min(1).optional(),
      }).strict(),
    )
    .max(MAX_STATE_RULES),
  unmapped: z.enum(["sync", "skip"]).default("sync"),
}).strict() satisfies z.ZodType<StateMapping, z.ZodTypeDef, unknown>;

/** The outcome of resolving one source state against a mapping. */
export interface StateDecision {
  action: StateAction;
  /** Stage id to write, or undefined to leave the destination's stage untouched. */
  stage?: string;
  /** The matched rule's `state` verbatim, or null when `unmapped` decided. */
  matched: string | null;
  /** Set only on a dry-run resolution: the decision the mapping WOULD have
   *  taken. The sibling fields carry the behavior that actually applies, so a
   *  caller that ignores `dryRun` is correct — it just logs nothing. */
  dryRun?: StateDecision;
}

function normalize(state: string | undefined): string {
  return (state ?? "").trim().toLowerCase();
}

/**
 * Resolve a source lifecycle state to its configured outcome (spec §6).
 *
 * Case-insensitive and whitespace-tolerant: the producer vocabulary is still in
 * flux, and an operator typing `Cancelled` against a producer sending
 * `CANCELLED` would otherwise silently no-op — the worst possible failure for a
 * rule whose job is to catch cancellations.
 *
 * A state no rule names, and an event carrying no state at all, both take
 * `unmapped`. The first matching rule wins; duplicates are rejected at save
 * time rather than resolved here.
 */
export function resolveState(mapping: StateMapping, state: string | undefined): StateDecision {
  const key = normalize(state);
  if (key !== "") {
    const rule = mapping.rules.find((r) => normalize(r.state) === key);
    if (rule) return { action: rule.action, stage: rule.stage, matched: rule.state };
  }
  return { action: mapping.unmapped, stage: undefined, matched: null };
}

/**
 * Build a mapping from a pair of `state → stage` records (spec §11).
 *
 * Rule ORDER carries the precedence: `overrides` come first, then `defaults`
 * for any state the overrides do not already name, so `resolveState`'s
 * first-match rule reproduces "operator override, then built-in" without a
 * second code path. De-duplication is case-insensitive, matching how the rules
 * themselves resolve.
 *
 * Used twice for the same reason: the deal transform normalizes its legacy
 * params through it at runtime, and the editor pre-fills through it on first
 * enable — so what the operator is shown is what the pipeline was already doing.
 */
export function stateMappingFromStageMap(
  overrides: Record<string, string> | undefined,
  defaults: Record<string, string> | undefined,
): StateMapping {
  const rules: StateRule[] = [];
  const named = new Set<string>();
  const add = (state: string, stage: string): void => {
    // The legacy `dealStageByStatus` is `z.record(z.string(), z.string())`, which
    // accepts a blank key and a blank value; `StateMappingSchema` accepts
    // neither. Dropping those entries here is what keeps a pre-fill built from a
    // legacy map SAVEABLE — emitting them would hand the operator a table the
    // API rejects, with no way to tell which row is at fault.
    if (state.trim() === "" || stage.trim() === "") return;
    const key = normalize(state);
    if (named.has(key)) return;
    named.add(key);
    rules.push({ state, action: "sync", stage });
  };
  for (const [state, stage] of Object.entries(overrides ?? {})) add(state, stage);
  for (const [state, stage] of Object.entries(defaults ?? {})) add(state, stage);
  // Blank entries are dropped above precisely so a pre-fill stays SAVEABLE; the
  // rule ceiling is the other half of that contract. Emitting more than the
  // schema accepts would hand the operator a table that fails on save with no
  // indication of which row is at fault, so refuse to build it at all.
  if (rules.length > MAX_STATE_RULES) {
    throw new RangeError(
      `legacy stage map has ${rules.length} distinct states, more than the ${MAX_STATE_RULES} a state mapping allows`,
    );
  }
  return { version: 1, rules, unmapped: "sync" };
}

/** What a transform's registry metadata says a state mapping may do to it. */
export interface StateMappingCapabilities {
  /** Destination property holding the record's lifecycle stage, from the
   *  transform's `systemProperties.stateField`. Absent ⇒ no rule may carry a stage. */
  stateField?: string;
  /** From the transform's `systemProperties.canArchive`. Falsy ⇒ no rule may archive. */
  canArchive?: boolean;
}

/**
 * Validate a mapping against what the destination object actually supports
 * (spec §8, blocking synchronous half). Empty result = valid.
 *
 * Every rule that reads as a request the destination cannot honor is rejected
 * with a real message rather than silently ignored — an operator who sets a
 * stage on a line item must be told it does nothing, not left believing a stage
 * is being written. The `unmapped: "archive"` case is not here: the schema's
 * enum makes it unrepresentable.
 */
export function validateStateMapping(
  mapping: StateMapping,
  opts: StateMappingCapabilities,
): Array<{ path: string; message: string }> {
  const issues: Array<{ path: string; message: string }> = [];
  const seen = new Set<string>();
  mapping.rules.forEach((rule, i) => {
    const key = normalize(rule.state);
    // `.min(1)` passes a whitespace-only state, but `resolveState` normalizes it
    // to "" and never looks it up — the rule would save cleanly and then be dead.
    if (key === "") {
      issues.push({ path: `rules.${i}.state`, message: "a status can't be blank" });
    } else {
      if (seen.has(key)) {
        issues.push({ path: `rules.${i}.state`, message: `"${rule.state}" already has a rule` });
      }
      seen.add(key);
    }

    if (rule.action === "archive" && opts.canArchive !== true) {
      issues.push({
        path: `rules.${i}.action`,
        message: "records of this kind cannot be removed — choose update or leave alone",
      });
    }
    if (rule.stage !== undefined) {
      if (opts.stateField === undefined) {
        issues.push({ path: `rules.${i}.stage`, message: "this record has no stage to set" });
      } else if (rule.action !== "sync") {
        issues.push({ path: `rules.${i}.stage`, message: "a stage can only be set when the action is to update the record" });
      }
    }
  });
  return issues;
}
